import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../../shared/contexts/LanguageContext';

export interface AutocompleteOption {
  id: string;
  name: string;
  name_ar?: string;
  [key: string]: any;
}

export interface AutocompleteFieldProps {
  label: string;
  placeholder: string;
  value: string;
  options: AutocompleteOption[];
  onChange: (selectedId: string, selectedOption: AutocompleteOption | null) => void;
  onSearch?: (query: string) => void;
  loading?: boolean;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  displayProperty?: 'name' | 'name_ar';
  className?: string;
}

const AutocompleteField: React.FC<AutocompleteFieldProps> = ({
  label,
  placeholder,
  value,
  options,
  onChange,
  onSearch,
  loading = false,
  error,
  required = false,
  disabled = false,
  displayProperty,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<AutocompleteOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<AutocompleteOption | null>(null);
  const [showAllOnFocus, setShowAllOnFocus] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { isRTL, language } = useLanguage();

  // Auto-select display property based on language if not explicitly set
  const effectiveDisplayProperty = displayProperty || (language === 'ar' ? 'name_ar' : 'name');

  // Update selected option when value changes
  useEffect(() => {
    if (value) {
      const option = options.find(opt => opt.id === value);
      setSelectedOption(option || null);
      if (option) {
        setSearchQuery(option[effectiveDisplayProperty] || option.name);
      }
    } else {
      setSelectedOption(null);
      setSearchQuery('');
    }
  }, [value, options, effectiveDisplayProperty]);

  // Filter options based on search query
  useEffect(() => {
    // Show all options if showAllOnFocus is true (user just focused on field with existing value)
    if (showAllOnFocus || !searchQuery.trim()) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option => {
        const searchFields = [
          option.name,
          option.name_ar,
          option.code
        ].filter(Boolean);

        return searchFields.some(field =>
          field?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      });
      setFilteredOptions(filtered);
    }
  }, [options, searchQuery, showAllOnFocus]);

  // Handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Disable showAllOnFocus when user starts typing
    if (showAllOnFocus) {
      setShowAllOnFocus(false);
    }

    // Call external search handler if provided
    if (onSearch) {
      onSearch(query);
    }

    // Open dropdown when typing
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  // Handle option selection
  const handleOptionSelect = (option: AutocompleteOption) => {
    setSelectedOption(option);
    setSearchQuery(option[effectiveDisplayProperty] || option.name);
    setIsOpen(false);
    setShowAllOnFocus(false); // Reset flag after selection
    onChange(option.id, option);
  };

  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true);
    // If there's an existing selection, show all options (don't filter by current text)
    if (selectedOption) {
      setShowAllOnFocus(true);
    }
  };

  // Handle input blur
  const handleInputBlur = (e: React.FocusEvent) => {
    // Delay closing to allow option click to register
    setTimeout(() => {
      if (!dropdownRef.current?.contains(e.relatedTarget as Node)) {
        setIsOpen(false);
        setShowAllOnFocus(false); // Reset flag when field loses focus

        // Reset search query to selected option if no valid selection made
        if (selectedOption) {
          setSearchQuery(selectedOption[effectiveDisplayProperty] || selectedOption.name);
        } else if (!value) {
          setSearchQuery('');
        }
      }
    }, 200); // Increased delay to ensure mousedown fires first
  };

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowAllOnFocus(false); // Reset flag when clicking outside
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleSearchChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || loading}
          className={`w-full px-4 py-3 pr-10 border rounded-xl transition-colors duration-200 ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : ''
          } ${
            error ? 'border-red-500' : 'border-gray-300 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20'
          }`}
        />
        
        {/* Loading indicator */}
        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#2E5D7D] border-t-transparent"></div>
          </div>
        )}
        
        {/* Dropdown arrow */}
        {!loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <svg
              className={`w-5 h-5 text-gray-400 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        )}
      </div>

      {/* Dropdown options */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-xl shadow-lg max-h-60 overflow-auto">
          {loading ? (
            <div className="px-4 py-3 text-center text-gray-500">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#2E5D7D] border-t-transparent mx-auto mb-2"></div>
              Loading...
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="px-4 py-3 text-center text-gray-500">
              {searchQuery ? 'No results found' : 'No options available'}
            </div>
          ) : (
            filteredOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent input from losing focus
                  handleOptionSelect(option);
                }}
                className={`w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0 ${
                  value === option.id ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                }`}
              >
                <div className="flex flex-col">
                  <span className="font-medium">{option[effectiveDisplayProperty] || option.name}</span>
                  {option.name_ar && option.name_ar !== option.name && effectiveDisplayProperty !== 'name_ar' && (
                    <span className="text-sm text-gray-500">{option.name_ar}</span>
                  )}
                  {option.name && effectiveDisplayProperty === 'name_ar' && option.name !== option.name_ar && (
                    <span className="text-sm text-gray-500">{option.name}</span>
                  )}
                  {option.code && (
                    <span className="text-xs text-gray-400">Code: {option.code}</span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      )}

      {/* Error message */}
      {error && (
        <p className="text-red-500 text-sm mt-1">{error}</p>
      )}
    </div>
  );
};

export default AutocompleteField;